import { Component, For, Show, createEffect, onCleanup } from 'solid-js'
import { createSignal } from 'solid-js'
import { AppSchema } from '/common/types'
import { getAssetUrl } from '../../util'
import { IconSearch } from '../Icons'
import './GenerateImageCharacterModal.css'

const GenerateImageCharacterModal: Component<{
  characters?: AppSchema.Character[]
  show: boolean
  hide: () => void
  onSelect?: (character: AppSchema.Character) => void
  selectedCharacter?: AppSchema.Character
}> = (props) => {
  const [searchQuery, setSearchQuery] = createSignal('')
  const [selected, setSelected] = createSignal<AppSchema.Character | undefined>(
    props.selectedCharacter
  )

  let inputRef: HTMLInputElement | undefined

  // Sync pending selection + clear search when modal opens; autofocus search input
  createEffect(() => {
    if (props.show) {
      setSelected(props.selectedCharacter)
      setSearchQuery('')
      setTimeout(() => inputRef?.focus(), 0)
    }
  })

  // Body scroll lock + ESC to close (without applying pending pick)
  createEffect(() => {
    if (props.show) {
      const prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') props.hide()
      }
      document.addEventListener('keydown', onKeyDown)
      onCleanup(() => {
        document.body.style.overflow = prevOverflow
        document.removeEventListener('keydown', onKeyDown)
      })
    }
  })

  /** Case-insensitive substring match on character name OR creator name */
  const filteredCharacters = () => {
    const query = searchQuery().toLowerCase().trim()
    if (!query) return props.characters || []
    return (props.characters || []).filter((c) => {
      const nameMatch = c.name.toLowerCase().includes(query)
      const creatorMatch = c.realName ? c.realName.toLowerCase().includes(query) : false
      return nameMatch || creatorMatch
    })
  }

  /** Apply pending pick and close */
  const handleConfirm = () => {
    const char = selected()
    if (char) props.onSelect?.(char)
    props.hide()
  }

  return (
    <Show when={props.show}>
      {/* ── Overlay — clicking outside closes without applying pick ── */}
      {/* backdrop-filter applied inline: autoprefixer strips the un-prefixed prop at build time */}
      <div
        class="gen-modal-overlay"
        style={{ 'backdrop-filter': 'blur(10px)', '-webkit-backdrop-filter': 'blur(10px)' }}
        onClick={() => props.hide()}
      >
        {/* ── Container — click inside does NOT close ── */}
        <div class="gen-modal" onClick={(e) => e.stopPropagation()}>
          {/* ── Close button: × glyph, closes without applying pick ── */}
          <button class="gen-modal-close" onClick={() => props.hide()} aria-label="Close">
            ×
          </button>

          {/* ── Title: Bebas Neue renders all-caps visually ── */}
          <p class="gen-modal-title">Pick a Character</p>

          {/* ── Subtitle ── */}
          <p class="gen-modal-sub">Choose who you want to generate</p>

          {/* ── Search ── */}
          <div class="gen-modal-search">
            <IconSearch size={18} />
            <input
              ref={(el) => {
                inputRef = el
              }}
              type="text"
              placeholder="Search characters..."
              value={searchQuery()}
              onInput={(e) => setSearchQuery(e.currentTarget.value)}
            />
          </div>

          {/* ── Scrollable tile grid ── */}
          <div class="gen-modal-grid">
            <For each={filteredCharacters()}>
              {(c) => {
                const isSelected = () => selected()?._id === c._id
                return (
                  <button
                    class="gen-char-tile"
                    classList={{ selected: isSelected() }}
                    onClick={() => setSelected(c)}
                  >
                    {c.avatar ? (
                      <img
                        src={getAssetUrl(c.avatar, true, { width: 194 * 2 })}
                        alt={c.name}
                        loading="lazy"
                      />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: '#13102a' }} />
                    )}
                    <div class="gen-char-tile-overlay" />
                    {/* Row 90: show character name (c.name maps to prototype's cat field) */}
                    <p class="gen-char-tile-name">{c.name}</p>
                  </button>
                )
              }}
            </For>

            {/* ── Empty state: only shown when query is active with no matches ── */}
            <Show when={filteredCharacters().length === 0 && searchQuery().length > 0}>
              <p class="gen-modal-empty">No characters match "{searchQuery()}"</p>
            </Show>
          </div>

          {/* ── Footer actions ── */}
          <div class="gen-modal-actions">
            {/* Cancel: closes without applying pending pick */}
            <button class="btn ghost" onClick={() => props.hide()}>
              Cancel
            </button>

            {/* Select: disabled when no pending pick; label includes character name */}
            <button
              class="btn primary"
              disabled={!selected()}
              onClick={handleConfirm}
            >
              {selected() ? `Select ${selected()!.name}` : 'Select'}
            </button>
          </div>
        </div>
      </div>
    </Show>
  )
}

export default GenerateImageCharacterModal
